import React from 'react';
import { Sparkles, Star } from 'lucide-react';

interface OfficialBearInsiderBadgeProps {
  className?: string;
  size?: 'small' | 'medium' | 'large';
  showLabel?: boolean;
  animated?: boolean;
}

// Custom Honeypot Icon Component
const HoneypotIcon = ({ className }: { className: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    {/* Honeypot body */}
    <path d="M12 2C8.5 2 6 4.5 6 8v2c0 1.5.5 3 1.5 4l1.5 2h6l1.5-2c1-1 1.5-2.5 1.5-4V8c0-3.5-2.5-6-6-6z" />
    {/* Honey drops */}
    <path d="M8 10h8" strokeWidth="1.5" />
    <path d="M9 12h6" strokeWidth="1.5" />
    <path d="M10 14h4" strokeWidth="1.5" />
    {/* Handle */}
    <path d="M10 2v2" strokeWidth="1.5" />
    <path d="M14 2v2" strokeWidth="1.5" />
    {/* Bees around honeypot */}
    <circle cx="6" cy="6" r="1" fill="currentColor" />
    <path d="M5 6h2" strokeWidth="0.5" />
    <circle cx="18" cy="8" r="1" fill="currentColor" />
    <path d="M17 8h2" strokeWidth="0.5" />
    <circle cx="4" cy="12" r="1" fill="currentColor" />
    <path d="M3 12h2" strokeWidth="0.5" />
  </svg>
);

export function OfficialBearInsiderBadge({ 
  className = "", 
  size = 'medium', 
  showLabel = true,
  animated = true 
}: OfficialBearInsiderBadgeProps) {
  const sizeClasses = {
    small: "text-xs px-2 py-1",
    medium: "text-sm px-3 py-2", 
    large: "text-base px-4 py-3"
  };

  const iconSizes = {
    small: "w-3 h-3",
    medium: "w-4 h-4", 
    large: "w-5 h-5"
  };

  return (
    <div className={`inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-400 via-yellow-500 to-orange-500 text-white font-semibold shadow-lg border-2 border-amber-300 ${sizeClasses[size]} ${animated ? 'animate-pulse hover:animate-none' : ''} ${className}`} data-testid="official-bear-insider-badge">
      <div className="relative">
        <HoneypotIcon className={`${iconSizes[size]} text-amber-100`} />
        {animated && (
          <Sparkles className={`${iconSizes[size]} absolute -top-1 -right-1 text-yellow-200 animate-bounce`} style={{ animationDelay: '0.5s' }} />
        )}
      </div>
      {showLabel && (
        <span className="font-bold tracking-wide" data-testid="official-bear-insider-badge-label">
          🐻 Official Bear Insider
        </span>
      )}
      {animated && (
        <Star className={`${iconSizes[size]} text-amber-100 animate-spin`} style={{ animationDuration: '3s' }} />
      )}
    </div>
  );
}

// Compact version for profile avatars and small spaces
export function OfficialBearInsiderShield({ 
  className = "", 
  size = 'medium',
  animated = true 
}: { 
  className?: string; 
  size?: 'small' | 'medium' | 'large';
  animated?: boolean;
}) {
  const iconSizes = {
    small: "w-4 h-4",
    medium: "w-5 h-5", 
    large: "w-6 h-6"
  };

  return (
    <div className={`inline-flex items-center justify-center rounded-full bg-gradient-to-r from-amber-400 via-yellow-500 to-orange-500 text-white shadow-lg border-2 border-amber-300 ${animated ? 'animate-pulse hover:animate-none' : ''} ${className}`} data-testid="official-bear-insider-shield">
      <div className="relative">
        <HoneypotIcon className={`${iconSizes[size]} text-amber-100`} />
        {animated && (
          <Sparkles className={`${iconSizes[size]} absolute -top-1 -right-1 text-yellow-200 animate-bounce`} style={{ animationDelay: '0.5s' }} />
        )}
      </div>
    </div>
  );
}

// Floating badge component for special occasions
export function FloatingOfficialBearInsiderBadge() {
  return (
    <div className="fixed bottom-6 right-6 z-50 animate-bounce">
      <OfficialBearInsiderBadge 
        size="medium" 
        animated={true} 
        showLabel={true}
        className="shadow-2xl hover:scale-105 transition-transform cursor-pointer"
      />
    </div>
  );
}
